// One encounter in detail, for the patient page (milestone L5.2): its clinical clock, the latest
// observations, orders with results, and which systems sent data. Pure.
//
// CLAUDE.md hard rule 3: every value here is shown as the source system sent it (a NEWS2 score
// only if the vitals system sent one). Nothing is calculated, scored or predicted. Hard rule 5:
// everything follows clinical time (occurred_at); `at` replays the encounter as it was then.

import type { Observation, Resource, ServiceRequest } from '@medplum/fhirtypes';
import { effectiveEvents, rebuild, type EncounterState } from './encounter.ts';
import type { AlertLevel, DomainEvent, EventSource, Location } from './events.ts';

export type TimelineKind =
  | 'arrival'
  | 'move'
  | 'admission'
  | 'departure'
  | 'lab'
  | 'imaging'
  | 'vitals'
  | 'alert'
  | 'record';

export interface TimelineItem {
  id: string;
  /** Clinical time. */
  at: string;
  kind: TimelineKind;
  /** The domain event type, e.g. "LabResulted". */
  type: DomainEvent['type'];
  title: string;
  detail?: string;
  source: EventSource;
  /** For alerts: the timer and level from the rules engine's event. */
  alert?: { timerId: string; level: AlertLevel | 'cleared' };
}

export interface ObservationValue {
  code: string;
  label: string;
  /** As sent, with a readable unit. */
  value: string;
}

export interface ObservationSet {
  at: string;
  device?: string;
  values: ObservationValue[];
}

export interface OrderResult {
  label: string;
  value: string;
  /** The source's abnormal flag (HL7 OBX-8 / FHIR interpretation), as sent. */
  flag?: string;
}

export interface OrderItem {
  order: string;
  name: string;
  status: string;
  /** Clinical time of the latest change. */
  at: string;
  results: OrderResult[];
}

export interface SourceSeen {
  source: EventSource;
  label: string;
  /** Clinical time of its latest event. */
  last: string;
  events: number;
}

export interface EncounterDetail {
  state: EncounterState;
  timeline: TimelineItem[];
  observations?: ObservationSet;
  /** The observation set before `observations`, so a page can say what changed. */
  previousObservations?: ObservationSet;
  labs: OrderItem[];
  imaging: OrderItem[];
  sources: SourceSeen[];
  /** The clinical time the detail is shown at (replay), or the latest event. */
  asOf?: string;
}

export const SOURCE_LABELS: Readonly<Record<EventSource, string>> = {
  adt: 'Patient administration (PAS)',
  lims: 'Laboratory (LIMS)',
  ris: 'Radiology (RIS)',
  pacs: 'Imaging archive (PACS)',
  vitals: 'Observations (e-obs)',
  epr: 'Electronic patient record (EPR)',
  beds: 'Bed management',
  rules: 'Diagnuvo rules engine',
  staff: 'Staff action (Diagnuvo board)',
};

const UNITS: Readonly<Record<string, string>> = {
  'mm[Hg]': 'mmHg',
  Cel: '°C',
  '/min': '/min',
  '%': '%',
  'L/min': 'L/min',
  '10*9/L': '×10⁹/L',
  '10*12/L': '×10¹²/L',
};

function place(location: Location | undefined): string | undefined {
  if (location === undefined) return undefined;
  const parts = [location.room, location.bed].filter((p) => p !== '');
  return [...new Set(parts)].join(' · ') || location.point_of_care;
}

function codeLabel(code: Observation['code'] | ServiceRequest['code']): string {
  const coding = code?.coding?.[0];
  return code?.text ?? coding?.display ?? coding?.code ?? 'Unnamed';
}

function observationValue(o: Observation): string | undefined {
  if (o.valueQuantity?.value !== undefined) {
    const unit = o.valueQuantity.code ?? o.valueQuantity.unit ?? '';
    const shown = UNITS[unit] ?? o.valueQuantity.unit ?? unit;
    const gap = shown === '' || shown === '%' ? '' : ' ';
    return `${o.valueQuantity.value}${gap}${shown}`;
  }
  if (o.valueInteger !== undefined) return String(o.valueInteger);
  if (o.valueString !== undefined) return o.valueString;
  if (o.valueBoolean !== undefined) return o.valueBoolean ? 'Yes' : 'No';
  if (o.valueCodeableConcept !== undefined) return codeLabel(o.valueCodeableConcept);
  const parts = (o.component ?? []).map((c) =>
    c.valueQuantity?.value === undefined ? undefined : String(c.valueQuantity.value),
  );
  if (parts.length > 0 && parts.every((p) => p !== undefined)) {
    const unit = o.component?.[0]?.valueQuantity?.code ?? '';
    return `${parts.join('/')} ${UNITS[unit] ?? unit}`.trim();
  }
  return undefined;
}

const observations = (resources: readonly Resource[]): Observation[] =>
  resources.filter((r): r is Observation => r.resourceType === 'Observation');

function orderName(event: DomainEvent): string | undefined {
  const request = event.fhir.find((r): r is ServiceRequest => r.resourceType === 'ServiceRequest');
  if (request !== undefined) return codeLabel(request.code);
  const data = event.data as { panel_code?: string; exam_code?: string };
  return data.panel_code ?? data.exam_code;
}

function results(event: DomainEvent): OrderResult[] {
  return observations(event.fhir).flatMap((o) => {
    const value = observationValue(o);
    if (value === undefined) return [];
    const flag = o.interpretation?.[0]?.coding?.[0]?.code ?? o.interpretation?.[0]?.text;
    return [{ label: codeLabel(o.code), value, ...(flag !== undefined && { flag }) }];
  });
}

function timelineItem(
  event: DomainEvent,
  names: ReadonlyMap<string, string>,
): TimelineItem | undefined {
  const base = { id: event.id, at: event.occurred_at, type: event.type, source: event.source };
  const data = event.data as {
    location?: Location;
    to?: Location;
    status?: string;
    device_id?: string;
    timer_id?: string;
    level?: AlertLevel;
    reason?: string;
    notify?: string;
  };
  const order = event.identifiers.placer_order;
  const name = (order === undefined ? undefined : names.get(order)) ?? orderName(event);
  const withDetail = (detail: string | undefined) => (detail === undefined ? {} : { detail });
  switch (event.type) {
    case 'Arrived':
      return {
        ...base,
        kind: 'arrival',
        title: 'Arrived in the ED',
        ...withDetail(place(data.location)),
      };
    case 'Moved': {
      const to = data.to;
      if (to?.kind === 'ward') {
        return {
          ...base,
          kind: 'departure',
          title: 'Left the ED for a ward',
          ...withDetail(place(to)),
        };
      }
      const title = to?.kind === 'triage' ? 'Moved to triage' : 'Moved';
      return { ...base, kind: 'move', title, ...withDetail(place(to)) };
    }
    case 'Admitted':
      return {
        ...base,
        kind: 'admission',
        title: 'Decision to admit',
        ...withDetail(place(data.location)),
      };
    case 'Discharged':
      return { ...base, kind: 'departure', title: 'Discharged from the ED' };
    case 'ArrivalCancelled':
      return { ...base, kind: 'departure', title: 'Arrival cancelled' };
    case 'DischargeCancelled':
      return { ...base, kind: 'move', title: 'Discharge cancelled' };
    case 'DetailsUpdated':
      return { ...base, kind: 'record', title: 'Patient details updated' };
    case 'LabOrdered':
      return { ...base, kind: 'lab', title: 'Lab test ordered', ...withDetail(name) };
    case 'LabSpecimenReceived':
      return { ...base, kind: 'lab', title: 'Lab sample received', ...withDetail(name) };
    case 'LabOrderCancelled':
      return { ...base, kind: 'lab', title: 'Lab order cancelled', ...withDetail(name) };
    case 'LabResulted':
      return {
        ...base,
        kind: 'lab',
        title: `Lab result (${data.status ?? 'reported'})`,
        ...withDetail(name),
      };
    case 'ImagingOrdered':
      return { ...base, kind: 'imaging', title: 'Imaging ordered', ...withDetail(name) };
    case 'ImagingStarted':
      return { ...base, kind: 'imaging', title: 'Imaging started', ...withDetail(name) };
    case 'ImagingCompleted':
      return { ...base, kind: 'imaging', title: 'Imaging completed', ...withDetail(name) };
    case 'ImagingCancelled':
      return { ...base, kind: 'imaging', title: 'Imaging cancelled', ...withDetail(name) };
    case 'ImagingReported':
      return {
        ...base,
        kind: 'imaging',
        title: `Imaging report (${data.status ?? 'reported'})`,
        ...withDetail(name),
      };
    case 'ImagingStudyAvailable':
      return { ...base, kind: 'imaging', title: 'Images available' };
    case 'VitalsRecorded':
      return {
        ...base,
        kind: 'vitals',
        title: 'Observations recorded',
        ...withDetail(data.device_id),
      };
    case 'EncounterUpdated':
    case 'PatientHistoryRecorded':
    case 'NoteRecorded':
      return { ...base, kind: 'record', title: 'EPR record updated' };
    case 'AmbulanceArrived':
      return { ...base, kind: 'arrival', title: 'Ambulance arrived' };
    case 'AmbulanceHandedOver':
      return { ...base, kind: 'arrival', title: 'Ambulance handover' };
    case 'AlertRaised':
      return {
        ...base,
        kind: 'alert',
        title: data.level === 'breach' ? 'Breach recorded' : 'Warning recorded',
        alert: { timerId: data.timer_id ?? '', level: data.level ?? 'warning' },
      };
    case 'AlertCleared':
      return {
        ...base,
        kind: 'alert',
        title: 'Alert cleared',
        alert: { timerId: data.timer_id ?? '', level: 'cleared' },
        ...withDetail(data.reason),
      };
    case 'AlertAcknowledged':
      return {
        ...base,
        kind: 'alert',
        title: data.level === 'breach' ? 'Breach acknowledged' : 'Warning acknowledged',
      };
    case 'AlertEscalated':
      return {
        ...base,
        kind: 'alert',
        title: 'Alert escalated',
        ...withDetail(data.notify),
      };
    case 'PatientsMerged':
    case 'PatientsUnmerged':
    case 'SourceMessageRemapped':
      return undefined;
  }
}

/** The encounter from all its events; with `at` (ISO 8601), as it was at that clinical time. */
export function encounterDetail(events: readonly DomainEvent[], at?: string): EncounterDetail {
  const cutoff = at === undefined ? undefined : Date.parse(at);
  const counted = effectiveEvents(
    cutoff === undefined ? events : events.filter((e) => Date.parse(e.occurred_at) <= cutoff),
  );
  const state = rebuild(counted);

  // An order's name from its first event with one (results often carry only a code).
  const names = new Map<string, string>();
  for (const e of counted) {
    const order = e.identifiers.placer_order;
    const request = e.fhir.some((r) => r.resourceType === 'ServiceRequest');
    if (order !== undefined && request && !names.has(order))
      names.set(order, orderName(e) ?? order);
  }

  const timeline = counted.flatMap((e) => {
    const item = timelineItem(e, names);
    return item === undefined ? [] : [item];
  });

  // The latest observation set, and the one before it (for "what changed" on the patient page).
  const vitalSets = counted.filter((e) => e.type === 'VitalsRecorded');
  const observationSet = toObservationSet(vitalSets.at(-1));
  const previousSet = toObservationSet(vitalSets.at(-2));

  const orders = (
    prefix: 'Lab' | 'Imaging',
    statuses: Record<string, { status: string; at: string }>,
  ) => {
    const byOrder = new Map<string, OrderItem>();
    for (const e of counted) {
      const order = e.identifiers.placer_order;
      if (order === undefined || !e.type.startsWith(prefix)) continue;
      const current = byOrder.get(order);
      const name = names.get(order) ?? current?.name ?? orderName(e) ?? order;
      const found = e.type === 'LabResulted' || e.type === 'ImagingReported' ? results(e) : [];
      byOrder.set(order, {
        order,
        name,
        status: statuses[order]?.status ?? 'ordered',
        at: statuses[order]?.at ?? e.occurred_at,
        results: found.length > 0 ? found : (current?.results ?? []),
      });
    }
    return [...byOrder.values()];
  };

  const seen = new Map<EventSource, SourceSeen>();
  for (const e of counted) {
    const s = seen.get(e.source);
    seen.set(e.source, {
      source: e.source,
      label: SOURCE_LABELS[e.source],
      last: e.occurred_at,
      events: (s?.events ?? 0) + 1,
    });
  }

  const asOf = at ?? counted.at(-1)?.occurred_at;
  return {
    state,
    timeline,
    ...(observationSet !== undefined && { observations: observationSet }),
    ...(previousSet !== undefined && { previousObservations: previousSet }),
    labs: orders('Lab', state.labs),
    imaging: orders('Imaging', state.imaging),
    sources: [...seen.values()],
    ...(asOf !== undefined && { asOf }),
  };
}

function toObservationSet(event: DomainEvent | undefined): ObservationSet | undefined {
  if (event === undefined) return undefined;
  const device = (event.data as { device_id?: string }).device_id;
  return {
    at: event.occurred_at,
    ...(device !== undefined && { device }),
    values: observations(event.fhir).flatMap((o) => {
      const value = observationValue(o);
      return value === undefined
        ? []
        : [{ code: o.code?.coding?.[0]?.code ?? '', label: codeLabel(o.code), value }];
    }),
  };
}
